import { useCallback, useEffect, useRef, useState } from 'react';

import { shallowEquals } from '../../../lib/shallowEquals';

export type DirtyFormController<T> = {
    value: T;
    setValue: (next: T | ((prev: T) => T)) => void;
    reset: () => void;
    isDirty: boolean;
};

export function useDirtyForm<T>(
    serverValue: T,
    equals: (a: T, b: T) => boolean = shallowEquals,
): DirtyFormController<T> {
    const [value, setValue] = useState<T>(serverValue);
    const lastServerRef = useRef<T>(serverValue);

    useEffect(() => {
        const previousServer = lastServerRef.current;
        if (equals(serverValue, previousServer)) {
            return;
        }
        setValue((current) => (equals(current, previousServer) ? serverValue : current));
        lastServerRef.current = serverValue;
    }, [serverValue, equals]);

    const reset = useCallback(() => {
        setValue(serverValue);
    }, [serverValue]);

    return {
        value,
        setValue,
        reset,
        isDirty: !equals(value, serverValue),
    };
}
